"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Icon } from "@/components/ui/icon";
import { useSubscription } from "../hooks/use-subscription";
import { useActionBarState } from "@/stores/use-action-bar-state";

const GRADIENT_HEIGHT = 200;
/** Initial guess for the docked-footer inset until the first measurement. */
const PAGINATOR_HEIGHT = 48;
const CARD_BOTTOM_OFFSET = 56;
const SCROLL_BOTTOM_THRESHOLD = 40;
const COLLAPSED_BAR_HEIGHT = 32;

interface FreePlanBannerProps {
  hasResults?: boolean;
}

/**
 * The vertical scroller the banner anchors to. The card list uses ScrollArea;
 * the table view uses ArTable's own scroll container.
 */
function findViewport(parent: HTMLElement): HTMLElement | null {
  return (
    parent.querySelector<HTMLElement>('[data-slot="scroll-area-viewport"]') ??
    parent.querySelector<HTMLElement>('[data-slot="ar-table-scroll"]')
  );
}

export function FreePlanBanner({ hasResults = false }: FreePlanBannerProps) {
  const router = useRouter();
  const { data: subscription, isLoading } = useSubscription();
  const containerRef = useRef<HTMLDivElement>(null);
  const [manualCollapsed, setManualCollapsed] = useState(false);
  const [atBottom, setAtBottom] = useState(false);
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [gradientIn, setGradientIn] = useState(false);
  // Everything docked under the results that the bar must clear: the
  // paginator (cards only — the table scrolls infinitely) and the table's
  // horizontal scrollbar (only when the columns overflow). Measured, not
  // assumed, because both come and go with the view.
  const [bottomInset, setBottomInset] = useState(PAGINATOR_HEIGHT);
  const actionBarActive = useActionBarState((s) => s.active);

  const showCard = !manualCollapsed && !atBottom && !actionBarActive;
  const showBar = manualCollapsed || atBottom || actionBarActive;
  const barIsFlat = atBottom;
  const barCanExpand = manualCollapsed && !atBottom && !actionBarActive;

  const isFreePlan =
    !isLoading &&
    (subscription === null ||
      subscription === undefined ||
      subscription.plan?.slug === "free");

  const shouldRender = isFreePlan && !isLoading && hasResults;

  useEffect(() => {
    if (!shouldRender) {
      setVisible(false);
      setEntered(false);
      setGradientIn(false);
      setAtBottom(false);
      return;
    }
    const delay = manualCollapsed ? 100 : 400;
    const t1 = setTimeout(() => setVisible(true), delay);
    const t2 = setTimeout(() => setGradientIn(true), delay + 100);
    const t3 = setTimeout(() => setEntered(true), delay + 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [shouldRender]); // manualCollapsed intentionally excluded — read on mount only

  useEffect(() => {
    if (!shouldRender) return;

    const parent = containerRef.current?.parentElement;
    if (!parent) return;

    const viewport = findViewport(parent);
    if (!viewport) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const isScrollable = scrollHeight > clientHeight + 10;
      const bottom =
        isScrollable &&
        scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_THRESHOLD;
      setAtBottom(bottom);
    };

    viewport.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();

    return () => viewport.removeEventListener("scroll", checkScroll);
  }, [shouldRender, hasResults]);

  useEffect(() => {
    if (!shouldRender) return;
    const parent = containerRef.current?.parentElement;
    if (!parent) return;

    let ro: ResizeObserver | null = null;
    const measure = () => {
      const docked = parent.querySelectorAll<HTMLElement>(
        '[data-slot="search-pagination"], [data-slot="ar-table-scrollbar"]',
      );
      let total = 0;
      ro?.disconnect();
      ro = new ResizeObserver(measure);
      docked.forEach((el) => {
        total += el.offsetHeight;
        ro?.observe(el);
      });
      setBottomInset(total);
    };
    measure();
    const mo = new MutationObserver(measure);
    mo.observe(parent, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      ro?.disconnect();
    };
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldRender) return;

    const parent = containerRef.current?.parentElement;
    if (!parent) return;

    const content = parent.querySelector<HTMLElement>(
      '[data-slot="scroll-area-content"]',
    );
    if (!content) return;

    content.style.paddingBottom = `${COLLAPSED_BAR_HEIGHT + 4}px`;
    return () => {
      content.style.paddingBottom = "";
    };
  }, [shouldRender]);

  const handleCollapse = useCallback(() => setManualCollapsed(true), []);
  const handleExpand = useCallback(() => setManualCollapsed(false), []);

  const handleUpgrade = useCallback(() => {
    router.push("/settings/plans-billing");
  }, [router]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const parent = containerRef.current?.parentElement;
    if (!parent) return;
    const viewport = findViewport(parent);
    if (!viewport) return;
    viewport.scrollTop += e.deltaY;
  }, []);

  return (
    <>
      <div ref={containerRef} className="hidden" />

      {shouldRender && visible && (
        <>
          {/* Darkening gradient */}
          <div
            className="absolute left-0 right-0 pointer-events-none z-[5] transition-opacity duration-500 ease-out"
            style={{
              bottom: bottomInset,
              height: actionBarActive ? 260 : GRADIENT_HEIGHT,
              opacity: (showCard && gradientIn) || actionBarActive ? 1 : 0,
            }}
          >
            <div
              className="w-full h-full"
              style={{
                background: actionBarActive
                  ? "linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--surface-primary) 85%, transparent) 60%, var(--surface-primary) 100%)"
                  : "linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--surface-primary) 70%, transparent) 100%)",
              }}
            />
          </div>

          {/* Collapsed bar */}
          <div
            className="absolute left-0 right-0 z-30 pointer-events-none transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{
              bottom: bottomInset,
              opacity: showBar ? 1 : 0,
              transform: showBar ? "translateY(0)" : "translateY(8px)",
            }}
          >
            <div
              className={`bg-[#66e278] flex items-center gap-2 px-3 py-1.5 transition-all duration-200 ${barIsFlat ? "" : "rounded-t-lg"} ${barCanExpand ? "cursor-pointer hover:bg-[#5dd46e]" : ""}`}
              style={{ pointerEvents: showBar ? "auto" : "none" }}
              onClick={barCanExpand ? handleExpand : undefined}
              onWheel={handleWheel}
            >
              <Icon
                name="IconAirdrop2"
                size={14}
                className="text-[#171717] shrink-0"
                stroke="1.5"
              />
              <span className="text-[13px] font-medium text-[#171717] flex-1 truncate">
                You&apos;re currently on the Free Plan
              </span>
              {barCanExpand && (
                <Icon
                  name="IconChevronTop"
                  size={14}
                  className="text-[#171717] shrink-0 opacity-60"
                  stroke="2"
                />
              )}
            </div>
          </div>

          {/* Expanded floating card */}
          <div
            className="absolute left-0 right-0 z-30 flex justify-center pointer-events-none"
            style={{
              bottom: showCard
                ? bottomInset + CARD_BOTTOM_OFFSET
                : bottomInset,
              opacity: showCard && entered ? 1 : 0,
              transform: showCard && entered
                ? "translateY(0) scale(1)"
                : showCard
                  ? "translateY(24px) scale(0.98)"
                  : "translateY(20px) scale(0.97)",
              transition: showCard
                ? "opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1), bottom 0.3s cubic-bezier(0.22, 1, 0.36, 1)"
                : "opacity 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), bottom 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            <div className="free-plan-glow-border relative rounded-lg max-w-md w-full mx-4 pointer-events-auto" onWheel={handleWheel}>
              <div className="free-plan-border-beam rounded-lg" />

              <div className="relative z-[1] rounded-lg overflow-hidden">
                {/* Top bar */}
                <div className="flex items-center gap-2.5 bg-[#66e278] px-2.5 py-1.5">
                  <div className="flex flex-1 items-center gap-1.5 min-w-0">
                    <Icon
                      name="IconAirdrop2"
                      size={16}
                      className="text-[#171717] shrink-0"
                      stroke="1.5"
                    />
                    <span className="text-sm font-medium text-[#171717] truncate leading-5">
                      You&apos;re currently on the Free Plan
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCollapse}
                    className="shrink-0 size-5 flex items-center justify-center text-[#171717] opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                    aria-label="Collapse"
                  >
                    <Icon name="IconChevronBottom" size={14} stroke="2" />
                  </button>
                </div>

                {/* Body */}
                <div
                  className="flex items-center gap-2.5 px-2.5 py-2.5"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(102, 226, 120, 0) 0%, rgba(102, 226, 120, 0.2) 100%), linear-gradient(90deg, #1a1a1c 0%, #1a1a1c 100%)",
                  }}
                >
                  <p className="flex-1 text-sm text-white tracking-[-0.42px] min-w-0">
                    Upgrade to unlock features &amp; credits
                  </p>
                  <button
                    type="button"
                    onClick={handleUpgrade}
                    className="shrink-0 flex items-center gap-1 bg-white rounded-md px-2.5 py-1 cursor-pointer hover:bg-white/90 transition-colors"
                  >
                    <span className="text-[13px] font-medium text-[#171717] px-1">
                      Upgrade Now
                    </span>
                    <Icon
                      name="IconArrowUpRight"
                      size={13}
                      className="text-[#171717]"
                      stroke="2"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
